import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Megaphone, History, Images } from "lucide-react";
import api from "./api";
import { refreshSiteContent } from "../store/useSiteContent";
import ImageUploader from "../components/ImageUploader";

/* Structured editors for the Dahlias home page:
   - Announcements: the panels shown right under the main banner
   - Timeline: the "Heritage since 1932" family archive entries
   Both save a JSON list into site_content and render live on /dahlias. */

const DEFAULT_ANNOUNCEMENT = () => ({
  id: `a-${Date.now()}`,
  kicker: "Announcement",
  heading: "",
  heading_italic: "",
  body: "",
  cta_label: "",
  cta_link: "",
  image: "",
  enabled: true,
});

const DEFAULT_TIMELINE = [
  { year: "1932", title: "Nana Jean is born", note: "The story begins on a small Aotearoa farm." },
  { year: "1950s", title: "First dahlia bed", note: "A handful of tubers, planted in a backyard row." },
  { year: "1980s", title: "The Mum's Shed", note: "A little red shed becomes the heart of the farm." },
  { year: "2020s", title: "Heritage Dahlias", note: "Her grandchildren grow the legacy on, by hand." },
  { year: "Today", title: "Carrying on", note: "Tubers shared a few hundred at a time." },
];

const DEFAULT_ANNOUNCEMENTS = [
  {
    id: "tubers-now",
    kicker: "From the shed",
    heading: "Tubers",
    heading_italic: "available now.",
    body: "Heirloom dahlia tubers, divided by hand in the Mum's Shed and ready to plant. First in, best dressed.",
    cta_label: "Shop the store",
    cta_link: "/dahlias/store",
    image: "",
    countdown_date: "2026-09-01",
    countdown_label: "Tuber sales open in",
    enabled: true,
  },
];

const DEFAULT_GALLERY = [
  { id: "field", image: "/photos/dahlia-field.jpg", caption: "The dahlia field in full summer flush.", enabled: true },
  { id: "cut", image: "/photos/cut-dahlias-spread.jpg", caption: "Fresh cut blooms, picked at first light.", enabled: true },
  { id: "pink", image: "/photos/open-pink-dahlias.jpg", caption: "Heirloom varieties, grown the slow way.", enabled: true },
  { id: "shed", image: "/photos/mums-shed.jpg", caption: "The Mum's Shed, the heart of the farm.", enabled: true },
];

function useJsonContent(key, fallback) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/site-content").then((r) => {
      try {
        const v = JSON.parse((r.data || {})[key] || "");
        setItems(Array.isArray(v) && v.length ? v : fallback);
      } catch {
        setItems(fallback);
      }
    }).catch(() => setItems(fallback)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return [items, setItems, loading];
}

async function saveJson(key, items) {
  await api.put(`/admin/site-content/${encodeURIComponent(key)}`, { value: JSON.stringify(items) });
  await refreshSiteContent();
}

const inputCls = "w-full px-3 py-2 bg-[var(--dahlias-cream)] border border-[var(--dahlias-ink)]/15 text-sm text-[var(--dahlias-ink)] rounded-sm";
const labelCls = "block text-[10px] tracking-[0.25em] uppercase text-[var(--dahlias-clay)] font-semibold mb-1";
const btnSmall = "p-1.5 rounded border border-[var(--dahlias-ink)]/15 text-[var(--dahlias-ink)]/60 hover:text-[var(--dahlias-forest)] hover:border-[var(--dahlias-forest)]";

export function HomeAnnouncementsEditor() {
  const [items, setItems, loading] = useJsonContent("dahlias.home.announcements", DEFAULT_ANNOUNCEMENTS);
  const [saving, setSaving] = useState(false);

  const patch = (i, p) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...p } : x)));
  const move = (i, dir) => setItems((xs) => {
    const next = [...xs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return xs;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const remove = (i) => {
    if (!window.confirm("Remove this announcement from the home page?")) return;
    setItems((xs) => xs.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveJson("dahlias.home.announcements", items);
      toast.success("Home page announcements saved, check /dahlias.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/25 p-5 md:p-7 wobble-a" data-testid="home-announcements-editor">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="font-display text-xl md:text-2xl text-[var(--dahlias-forest)] inline-flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#6E1E3A]" /> Home page announcements
        </h2>
        <a href="/dahlias" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-[var(--dahlias-sage-deep)] hover:text-[var(--dahlias-forest)]">
          <Eye className="w-3 h-3" /> View live page →
        </a>
      </div>
      <p className="text-sm italic text-[var(--dahlias-ink)]/60 mb-5">
        These panels appear right under the main banner on the Dahlias home page. Add as many as you like,
        change the picture, the words and where the button links. Toggle one off to hide it without deleting it.
      </p>

      {loading ? (
        <div className="inline-flex items-center gap-2 text-[var(--dahlias-ink)]/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-5">
          {(items || []).map((a, i) => (
            <div key={a.id || i} className={`bg-white border rounded p-4 ${a.enabled === false ? "opacity-60 border-dashed" : "border-[var(--dahlias-sage)]/30"}`} data-testid={`announcement-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Panel {i + 1}{a.enabled === false ? " · hidden" : ""}</span>
                <div className="flex gap-1.5">
                  <button type="button" title={a.enabled === false ? "Show on site" : "Hide from site"} onClick={() => patch(i, { enabled: a.enabled === false })} className={btnSmall} data-testid={`announcement-toggle-${i}`}>
                    {a.enabled === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button type="button" title="Move up" onClick={() => move(i, -1)} className={btnSmall}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Move down" onClick={() => move(i, 1)} className={btnSmall}><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Delete" onClick={() => remove(i)} className={`${btnSmall} hover:!text-red-700 hover:!border-red-700`} data-testid={`announcement-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Small label (top)</label>
                    <input className={inputCls} value={a.kicker || ""} onChange={(e) => patch(i, { kicker: e.target.value })} placeholder="Limited release" data-testid={`announcement-kicker-${i}`} />
                  </div>
                  <div>
                    <label className={labelCls}>Big heading</label>
                    <input className={inputCls} value={a.heading || ""} onChange={(e) => patch(i, { heading: e.target.value })} placeholder="Mystery Boxes" data-testid={`announcement-heading-${i}`} />
                  </div>
                  <div>
                    <label className={labelCls}>Italic second line</label>
                    <input className={inputCls} value={a.heading_italic || ""} onChange={(e) => patch(i, { heading_italic: e.target.value })} placeholder="are back." />
                  </div>
                  <div>
                    <label className={labelCls}>Body text</label>
                    <textarea rows={3} className={inputCls} value={a.body || ""} onChange={(e) => patch(i, { body: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Button text</label>
                    <input className={inputCls} value={a.cta_label || ""} onChange={(e) => patch(i, { cta_label: e.target.value })} placeholder="Reserve a box" />
                  </div>
                  <div>
                    <label className={labelCls}>Button links to</label>
                    <input className={inputCls} value={a.cta_link || ""} onChange={(e) => patch(i, { cta_link: e.target.value })} placeholder="/dahlias/mystery-boxes or https://…" data-testid={`announcement-link-${i}`} />
                    <p className="text-[10px] text-[var(--dahlias-ink)]/50 mt-1">Use a site path like /dahlias/store, or a full https:// link.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Countdown date (optional)</label>
                      <input type="date" className={inputCls} value={a.countdown_date || ""} onChange={(e) => patch(i, { countdown_date: e.target.value })} data-testid={`announcement-countdown-date-${i}`} />
                    </div>
                    <div>
                      <label className={labelCls}>Countdown label</label>
                      <input className={inputCls} value={a.countdown_label || ""} onChange={(e) => patch(i, { countdown_label: e.target.value })} placeholder="Tuber sales open in" data-testid={`announcement-countdown-label-${i}`} />
                    </div>
                    <p className="col-span-2 text-[10px] text-[var(--dahlias-ink)]/50 -mt-1">Shows a "{"days remaining"}" pill on the tile until the date. Clear the date to hide it.</p>
                  </div>
                  <ImageUploader value={a.image || ""} onChange={(url) => patch(i, { image: url })} label="Side picture (optional)" testid={`announcement-image-${i}`} />
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => setItems((xs) => [...(xs || []), DEFAULT_ANNOUNCEMENT()])} className="inline-flex items-center gap-2 px-4 h-10 border-2 border-dashed border-[var(--dahlias-sage)] text-[var(--dahlias-sage-deep)] text-sm hover:bg-[var(--dahlias-sage)]/10 wobble-pill" data-testid="announcement-add">
              <Plus className="w-4 h-4" /> Add announcement
            </button>
            <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-10 bg-[#6E1E3A] text-white text-sm hover:bg-[#5A1830] transition disabled:opacity-50 wobble-pill" data-testid="announcements-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save announcements
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function HomeGalleryEditor() {
  const [items, setItems, loading] = useJsonContent("dahlias.home.gallery", DEFAULT_GALLERY);
  const [saving, setSaving] = useState(false);
  const massRef = React.useRef(null);

  const massBtn = () => massRef.current?.click();
  const massUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSaving(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: `g-${Date.now()}-${added.length}`, image: res.data.url, caption: "", enabled: true });
      } catch (err) {
        toast.error(`Upload failed for ${f.name}: ${err?.response?.data?.detail || "error"}`);
      }
    }
    if (added.length) {
      setItems((xs) => [...(xs || []), ...added]);
      toast.success(`${added.length} photo(s) added below , add captions if you like, then hit Save gallery.`);
    }
    setSaving(false);
    if (massRef.current) massRef.current.value = "";
  };

  const patch = (i, p) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...p } : x)));
  const move = (i, dir) => setItems((xs) => {
    const next = [...xs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return xs;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const remove = (i) => {
    if (!window.confirm("Remove this photo from the home page gallery?")) return;
    setItems((xs) => xs.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveJson("dahlias.home.gallery", (items || []).filter((p) => p.image));
      toast.success("Home page gallery saved, check /dahlias.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 p-5 md:p-7 wobble-b" data-testid="home-gallery-editor">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="font-display text-xl md:text-2xl text-[var(--dahlias-forest)] inline-flex items-center gap-2">
          <Images className="w-5 h-5 text-[#6E1E3A]" /> Home page gallery
        </h2>
        <a href="/dahlias" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-[var(--dahlias-sage-deep)] hover:text-[var(--dahlias-forest)]">
          <Eye className="w-3 h-3" /> View live page →
        </a>
      </div>
      <p className="text-sm italic text-[var(--dahlias-ink)]/60 mb-5">
        The big rotating photo gallery on the Dahlias home page. Add photos, write a little caption for each,
        drag the order with the arrows. Photos rotate automatically every 5 seconds on the site.
      </p>

      {loading ? (
        <div className="inline-flex items-center gap-2 text-[var(--dahlias-ink)]/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-4">
          {(items || []).map((p, i) => (
            <div key={p.id || i} className={`bg-white border rounded p-4 ${p.enabled === false ? "opacity-60 border-dashed" : "border-[var(--dahlias-sage)]/30"}`} data-testid={`gallery-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Photo {i + 1}{p.enabled === false ? " · hidden" : ""}</span>
                <div className="flex gap-1.5">
                  <button type="button" title={p.enabled === false ? "Show on site" : "Hide from site"} onClick={() => patch(i, { enabled: p.enabled === false })} className={btnSmall} data-testid={`gallery-toggle-${i}`}>
                    {p.enabled === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button type="button" title="Move up" onClick={() => move(i, -1)} className={btnSmall} data-testid={`gallery-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Move down" onClick={() => move(i, 1)} className={btnSmall} data-testid={`gallery-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button type="button" title="Delete" onClick={() => remove(i)} className={`${btnSmall} hover:!text-red-700 hover:!border-red-700`} data-testid={`gallery-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 items-start">
                <ImageUploader value={p.image || ""} onChange={(url) => patch(i, { image: url })} label="Photo" testid={`gallery-image-${i}`} />
                <div>
                  <label className={labelCls}>Caption (optional)</label>
                  <input className={inputCls} value={p.caption || ""} onChange={(e) => patch(i, { caption: e.target.value })} placeholder="The dahlia field in full summer flush." data-testid={`gallery-caption-${i}`} />
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={massBtn} disabled={saving} className="inline-flex items-center gap-2 px-4 h-10 border-2 border-dashed border-[#6E1E3A] text-[#6E1E3A] text-sm hover:bg-[#6E1E3A]/5 wobble-pill" data-testid="gallery-mass-upload-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Mass upload photos
            </button>
            <input ref={massRef} type="file" accept="image/*" multiple className="hidden" onChange={massUpload} data-testid="gallery-mass-upload-input" />
            <button type="button" onClick={() => setItems((xs) => [...(xs || []), { id: `g-${Date.now()}`, image: "", caption: "", enabled: true }])} className="inline-flex items-center gap-2 px-4 h-10 border-2 border-dashed border-[var(--dahlias-sage)] text-[var(--dahlias-sage-deep)] text-sm hover:bg-[var(--dahlias-sage)]/10 wobble-pill" data-testid="gallery-add">
              <Plus className="w-4 h-4" /> Add photo
            </button>
            <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-10 bg-[#6E1E3A] text-white text-sm hover:bg-[#5A1830] transition disabled:opacity-50 wobble-pill" data-testid="gallery-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save gallery
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function TimelineEditor() {
  const [items, setItems, loading] = useJsonContent("dahlias.home.timeline", DEFAULT_TIMELINE);
  const [saving, setSaving] = useState(false);

  const patch = (i, p) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...p } : x)));
  const move = (i, dir) => setItems((xs) => {
    const next = [...xs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return xs;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await saveJson("dahlias.home.timeline", items.filter((t) => t.year || t.title));
      toast.success("Timeline saved, check /dahlias.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 p-5 md:p-7 wobble-b" data-testid="timeline-editor">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="font-display text-xl md:text-2xl text-[var(--dahlias-forest)] inline-flex items-center gap-2">
          <History className="w-5 h-5 text-[#6E1E3A]" /> Heritage timeline
        </h2>
        <a href="/dahlias" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-[var(--dahlias-sage-deep)] hover:text-[var(--dahlias-forest)]">
          <Eye className="w-3 h-3" /> View live page →
        </a>
      </div>
      <p className="text-sm italic text-[var(--dahlias-ink)]/60 mb-5">
        The "Heritage since 1932" family archive at the bottom of the Dahlias home page. Fix any wording here.
      </p>

      {loading ? (
        <div className="inline-flex items-center gap-2 text-[var(--dahlias-ink)]/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {(items || []).map((t, i) => (
            <div key={i} className="bg-white border border-[var(--dahlias-sage)]/30 rounded p-3 grid grid-cols-1 md:grid-cols-[110px_1fr_2fr_auto] gap-2 items-start" data-testid={`timeline-row-${i}`}>
              <input className={inputCls} value={t.year || ""} onChange={(e) => patch(i, { year: e.target.value })} placeholder="1932" data-testid={`timeline-year-${i}`} />
              <input className={inputCls} value={t.title || ""} onChange={(e) => patch(i, { title: e.target.value })} placeholder="Title" data-testid={`timeline-title-${i}`} />
              <input className={inputCls} value={t.note || ""} onChange={(e) => patch(i, { note: e.target.value })} placeholder="One line of story" data-testid={`timeline-note-${i}`} />
              <div className="flex gap-1.5">
                <button type="button" onClick={() => move(i, -1)} className={btnSmall}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} className={btnSmall}><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => setItems((xs) => xs.filter((_, idx) => idx !== i))} className={`${btnSmall} hover:!text-red-700 hover:!border-red-700`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => setItems((xs) => [...(xs || []), { year: "", title: "", note: "" }])} className="inline-flex items-center gap-2 px-4 h-10 border-2 border-dashed border-[var(--dahlias-sage)] text-[var(--dahlias-sage-deep)] text-sm hover:bg-[var(--dahlias-sage)]/10 wobble-pill" data-testid="timeline-add">
              <Plus className="w-4 h-4" /> Add entry
            </button>
            <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-10 bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)] text-sm hover:bg-[var(--dahlias-sage-deep)] transition disabled:opacity-50 wobble-pill" data-testid="timeline-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save timeline
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

